import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { companyService } from '../services/companyService';
import { useAuthStore } from '../stores/useAuthStore';
import toast from 'react-hot-toast';
import { motion, useReducedMotion, type Variants } from 'motion/react';

import { Button, Badge, Card, Input, Select, Modal } from '../components/ui';

import { Company, CompanyUser } from '../types/company';
import { Plan } from '../types/plan';

// Page-load entrance variants — matches LoginPage Phase 5 pattern.
// Duration values mirror --snapfy-duration-base (300ms) and --snapfy-duration-slow (500ms).
// Easing mirrors --snapfy-ease-standard cubic-bezier(0.22,1,0.36,1).
const headerVariants: Variants = {
    hidden: { opacity: 0, y: -12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const contentVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
} as const;

const OrganizationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user: authUser } = useAuthStore();
    const prefersReducedMotion = useReducedMotion();
    const [company, setCompany] = useState<Company | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'consumption' | 'billing'>('overview');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [resettingUserId, setResettingUserId] = useState<string | null>(null);
    const [togglingActiveUserId, setTogglingActiveUserId] = useState<string | null>(null);
    const [isTogglingSharingMode, setIsTogglingSharingMode] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<Array<{ id: string; email: string; role: string; status: string; createdAt: string; expiresAt: string }>>([]);

    const [plans, setPlans] = useState<Plan[]>([]);

    const [usageReport, setUsageReport] = useState<any>(null);

    useEffect(() => {
        fetchCompany();
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/subscriptions/plans');
            setPlans(response.data.filter((p: Plan) => p.name.includes('Business') || (p as any).type === 'business'));
        } catch (error) {
            console.error('Failed to fetch plans', error);
        }
    };

    // Fetch usage report when tab is active
    useEffect(() => {
        if (activeTab === 'consumption') {
            fetchUsageReport();
        }
    }, [activeTab]);

    const [usageReportError, setUsageReportError] = useState<string | null>(null);

    const fetchUsageReport = async () => {
        setUsageReportError(null);
        try {
            const response = await api.get('/companies/usage_report');
            setUsageReport(response.data);
        } catch (error: any) {
            console.error('Failed to fetch usage report', error);
            if (error.response?.status === 403) {
                setUsageReportError('Apenas administradores da empresa podem ver o relatório de consumo detalhado.');
            } else {
                setUsageReportError('Erro ao carregar relatório de consumo.');
            }
        }
    }

    const handleSubscribe = async (planId: string) => {
        try {
            const response = await api.post(`/company/subscribe/${planId}`);
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao iniciar assinatura.');
        }
    };

    const fetchCompany = async () => {
        try {
            const response = await api.get('/company/my');
            setCompany(response.data);
            // Fetch pending invites
            if (authUser?.companyRole === 'admin') {
                try {
                    const usersResponse = await api.get('/company/users');
                    const data = usersResponse.data as { pendingInvites?: Array<{ id: string; email: string; role: string; status: string; createdAt: string; expiresAt: string }> };
                    setPendingInvites(data.pendingInvites ?? []);
                } catch {
                    // Silently fail — invites are supplementary
                }
            }
        } catch (error: unknown) {
            const err = error as { response?: { status?: number } };
            if (err.response?.status === 404) {
                navigate('/register-company');
            } else {
                toast.error('Erro ao carregar dados da empresa.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (userId: string) => {
        const confirmed = window.confirm(
            'A senha atual deste usuário será descontinuada e uma nova senha temporária será enviada por email. Deseja continuar?'
        );
        if (!confirmed) return;

        setResettingUserId(userId);
        try {
            await api.post(`/company/users/${userId}/reset-password`);
            toast.success('Nova senha enviada por email ao usuário.');
            fetchCompany();
        } catch (error) {
            toast.error('Erro ao resetar senha.');
        } finally {
            setResettingUserId(null);
        }
    };

    const handleToggleUserActive = async (user: CompanyUser) => {
        setTogglingActiveUserId(user.id);
        try {
            await companyService.toggleUserActive(user.id);
            const isNowActive = user.isActiveInCompany === false;
            toast.success(isNowActive ? 'Usuário ativado.' : 'Usuário inativado.');
            fetchCompany();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erro ao alterar status do usuário.');
        } finally {
            setTogglingActiveUserId(null);
        }
    };

    const handleToggleSharingMode = async () => {
        if (!company) return;
        const newMode = company.sharingMode === 'isolated' ? 'shared' : 'isolated';
        setIsTogglingSharingMode(true);
        try {
            await companyService.update(company.id, { sharing_mode: newMode } as any);
            toast.success(newMode === 'shared' ? 'Galeria compartilhada ativada.' : 'Galeria isolada — cada membro vê apenas seus conteúdos.');
            fetchCompany();
        } catch {
            toast.error('Erro ao alterar modo de compartilhamento.');
        } finally {
            setIsTogglingSharingMode(false);
        }
    };

    if (isLoading) return <div className="p-8 text-snapfy-text-primary">Carregando...</div>;
    if (!company) return null;

    return (
        <div className="container mx-auto px-4 py-8 text-snapfy-text-primary">
            {/* Header — page-load entrance (one per page, useReducedMotion-guarded) */}
            <motion.div
                variants={prefersReducedMotion
                    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                    : headerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* bg-[linear-gradient(to_right,…)] uses gradient stop hex — sanctioned residual:
                    #1a0a3e is a brand gradient stop (Phase 7 scope). indigo-900/50 is a Tailwind
                    named color — not a raw hex. border-snapfy-primary/30 replaces border-[#7063f7]/30. */}
                <div className="bg-[linear-gradient(to_right,rgba(26,10,62,0.5),rgba(49,46,129,0.5))] rounded-card p-6 mb-6 border border-snapfy-primary/30">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold">{company.name}</h1>
                            <p className="text-snapfy-text-secondary">{company.tradeName || company.document}</p>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-snapfy-text-secondary">Créditos Disponíveis</div>
                            <div className="text-3xl font-bold text-snapfy-accent-soft">{company.creditsBalance}</div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Tabs — re-styled with tokens; border-snapfy-primary replaces border-[#7063f7];
                text-snapfy-accent-soft replaces text-snapfy-accent-soft. No Chip/NavItem for these
                page-level tabs — token restyle suffices and keeps the border-b underline pattern. */}
            <div className="flex border-b border-snapfy-border mb-6">
                {(['overview', 'users', 'consumption', ...(authUser?.companyRole === 'admin' ? ['billing'] : [])] as const).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as 'overview' | 'users' | 'consumption' | 'billing')}
                        className={`py-3 px-6 font-medium capitalize focus-visible:outline-none focus-visible:ring-snapfy-focus ${activeTab === tab
                            ? 'border-b-2 border-snapfy-primary text-snapfy-accent-soft'
                            : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                    >
                        {tab === 'overview' ? 'Visão Geral' : tab === 'users' ? 'Usuários' : tab === 'consumption' ? 'Consumo' : 'Faturamento'}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <motion.div
                className="bg-snapfy-surface rounded-card border border-snapfy-border p-6"
                variants={prefersReducedMotion
                    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                    : contentVariants}
                initial="hidden"
                animate="visible"
            >
                {activeTab === 'overview' && (
                    <div className="space-y-8">
                        {/* Dados da Organização + Plano */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Dados da Organização</h3>
                                <div className="space-y-3">
                                    <div>
                                        <span className="text-snapfy-text-secondary text-sm">Nome:</span>
                                        <p>{company.name}</p>
                                    </div>
                                    <div>
                                        <span className="text-snapfy-text-secondary text-sm">Email Financeiro:</span>
                                        <p>{company.billingEmail}</p>
                                    </div>
                                    <div>
                                        <span className="text-snapfy-text-secondary text-sm">Telefone:</span>
                                        <p>{company.phone || 'Não informado'}</p>
                                    </div>
                                    <div>
                                        <span className="text-snapfy-text-secondary text-sm">Membros:</span>
                                        <p>{company.usersCount || company.users?.length || 0}</p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Plano Atual</h3>
                                {/* #1a0a3e is a brand gradient stop — sanctioned residual (Phase 7).
                                    border-snapfy-primary/30 replaces border-[#7063f7]/30. */}
                                <Card className="bg-[rgba(26,10,62,0.30)] p-4">
                                    <p className="text-xl font-bold text-snapfy-accent-soft">
                                        {company.activeSubscription?.plan?.name || 'Sem Plano Ativo'}
                                    </p>
                                    {authUser?.companyRole === 'admin' && (
                                        <button
                                            onClick={() => setActiveTab('billing')}
                                            className="mt-3 text-sm text-snapfy-accent-soft hover:underline focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                                        >
                                            Gerenciar Plano →
                                        </button>
                                    )}
                                </Card>
                            </div>
                        </div>

                        {/* Dados Fiscais para NF — only visible to company admins */}
                        {authUser?.companyRole === 'admin' && <div className="border-t border-snapfy-border pt-6">
                            <h3 className="text-lg font-semibold mb-4">Dados Fiscais para NF</h3>
                            {company.document || company.address ? (
                                <div className="space-y-3">
                                    {company.document && (
                                        <div>
                                            <span className="text-snapfy-text-secondary text-sm">CNPJ:</span>
                                            <p>{company.document}</p>
                                        </div>
                                    )}
                                    {company.address && (
                                        <div>
                                            <span className="text-snapfy-text-secondary text-sm">Endereço:</span>
                                            <p>{company.address}, {company.addressNumber} {company.addressComplement}</p>
                                            <p>{company.neighborhood} - {company.city}/{company.state}</p>
                                            <p>{company.postalCode}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-card p-4">
                                    <p className="text-yellow-300 text-sm">
                                        Dados fiscais não informados.{' '}
                                        <button
                                            onClick={() => navigate('/register-company')}
                                            className="text-snapfy-accent-soft hover:underline focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                                        >
                                            Completar dados fiscais →
                                        </button>
                                    </p>
                                </div>
                            )}
                        </div>}

                        {/* Compartilhamento */}
                        {authUser?.companyRole === 'admin' && (
                            <div className="border-t border-snapfy-border pt-6">
                                <h3 className="text-lg font-semibold mb-4">Compartilhamento de Conteúdo</h3>
                                <div className="bg-snapfy-surface-hover rounded-card p-4 border border-snapfy-border">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">
                                                {company.sharingMode === 'isolated' ? 'Modo Isolado' : 'Galeria Compartilhada'}
                                            </p>
                                            <p className="text-sm text-snapfy-text-secondary mt-1">
                                                {company.sharingMode === 'isolated'
                                                    ? 'Cada membro vê apenas seus próprios conteúdos gerados.'
                                                    : 'Membros podem ver conteúdos gerados por toda a equipe.'}
                                            </p>
                                        </div>
                                        {/* Toggle switch — native button preserved; bg-snapfy-primary replaces bg-[#7063f7] */}
                                        <button
                                            onClick={handleToggleSharingMode}
                                            disabled={isTogglingSharingMode}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus ${
                                                company.sharingMode !== 'isolated' ? 'bg-snapfy-primary' : 'bg-snapfy-inner'
                                            } ${isTogglingSharingMode ? 'opacity-50' : ''}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                company.sharingMode !== 'isolated' ? 'translate-x-6' : 'translate-x-1'
                                            }`} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'users' && (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">Membros da Equipe</h3>
                                {authUser?.companyRole === 'admin' && (
                                    <p className="text-sm text-snapfy-text-secondary mt-1">
                                        {(company.users?.length || 0) + pendingInvites.length} / 10 vagas utilizadas
                                        {pendingInvites.length > 0 && ` (${pendingInvites.length} convite${pendingInvites.length > 1 ? 's' : ''} pendente${pendingInvites.length > 1 ? 's' : ''})`}
                                    </p>
                                )}
                            </div>
                            {authUser?.companyRole === 'admin' && (
                                <div className="flex items-center gap-2">
                                    {(company.users?.length || 0) + pendingInvites.length >= 10 ? (
                                        <span className="text-yellow-400 text-sm">Limite de 10 usuários atingido</span>
                                    ) : (
                                        <>
                                            <Button
                                                variant="solid"
                                                onClick={() => setShowInviteModal(true)}
                                            >
                                                Convidar Usuário
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => setShowCreateUserModal(true)}
                                            >
                                                Criar Usuário
                                            </Button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <table className="min-w-full">
                            <thead>
                                <tr className="text-snapfy-text-secondary border-b border-snapfy-border">
                                    <th className="text-left py-3">Nome</th>
                                    <th className="text-left py-3">Email</th>
                                    <th className="text-left py-3">Cargo</th>
                                    <th className="text-left py-3">Consumo 30d</th>
                                    {authUser?.companyRole === 'admin' && <th className="text-left py-3">Ações</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {company.users?.map((user) => {
                                    const userIsOwner = String(user.id) === String(company.ownerId) || user.companyRole === 'owner';
                                    const isInactive = user.isActiveInCompany === false;
                                    return (
                                    <tr key={user.id} className={`border-b border-snapfy-border/50 ${isInactive ? 'opacity-50' : ''}`}>
                                        <td className="py-3">{user.name}</td>
                                        <td className="py-3 text-snapfy-text-secondary">{user.email}</td>
                                        <td className="py-3">
                                            <span className="flex items-center gap-2">
                                                {/* Role badge — info variant for admin, plain surface chip for member */}
                                                {user.companyRole === 'admin' ? (
                                                    <Badge variant="info">{user.companyRole}</Badge>
                                                ) : (
                                                    <span className="px-2 py-1 rounded text-xs bg-snapfy-inner text-snapfy-text-secondary">
                                                        {user.companyRole || 'member'}
                                                    </span>
                                                )}
                                                {isInactive && (
                                                    <Badge variant="error">Inativo</Badge>
                                                )}
                                            </span>
                                        </td>
                                        <td className="py-3 text-snapfy-text-secondary">—</td>
                                        {authUser?.companyRole === 'admin' && <td className="py-3 flex items-center gap-2">
                                                    {String(user.id) !== String(authUser?.id) && (
                                                        <button
                                                            onClick={() => handleResetPassword(user.id)}
                                                            disabled={resettingUserId === user.id}
                                                            className="text-yellow-400 hover:text-yellow-300 text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                                                            title="Gerar nova senha temporária e enviar por email"
                                                        >
                                                            {resettingUserId === user.id ? (
                                                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                </svg>
                                                            )}
                                                            {resettingUserId === user.id ? 'Enviando...' : 'Gerar senha'}
                                                        </button>
                                                    )}
                                                    {!userIsOwner && (
                                                        <button
                                                            onClick={() => handleToggleUserActive(user)}
                                                            disabled={togglingActiveUserId === user.id}
                                                            className={`text-xs px-2 py-1 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus ${
                                                                isInactive
                                                                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                                                    : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                                                            }`}
                                                        >
                                                            {togglingActiveUserId === user.id
                                                                ? '...'
                                                                : isInactive
                                                                    ? 'Ativar'
                                                                    : 'Inativar'}
                                                        </button>
                                                    )}
                                        </td>}
                                    </tr>
                                    );
                                })}
                                {/* Pending Invites as table rows */}
                                {authUser?.companyRole === 'admin' && pendingInvites.map((invite) => (
                                    <tr key={`invite-${invite.id}`} className="border-b border-snapfy-border/50 bg-yellow-500/5">
                                        <td className="py-3 text-snapfy-text-secondary italic">—</td>
                                        <td className="py-3 text-snapfy-text-secondary">{invite.email}</td>
                                        <td className="py-3">
                                            <Badge variant="warning">Convite pendente</Badge>
                                        </td>
                                        <td className="py-3 text-snapfy-text-secondary">—</td>
                                        <td className="py-3 text-snapfy-text-secondary">—</td>
                                        <td className="py-3">
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        await api.delete(`/company/invites/${invite.id}`);
                                                        toast.success('Convite cancelado.');
                                                        fetchCompany();
                                                    } catch {
                                                        toast.error('Erro ao cancelar convite.');
                                                    }
                                                }}
                                                className="text-xs px-2 py-1 rounded font-medium bg-snapfy-error/20 text-snapfy-error hover:bg-snapfy-error/30 transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus"
                                            >
                                                Cancelar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'consumption' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold">Relatório de Consumo (Últimos 30 dias)</h3>
                        </div>

                        {usageReportError ? (
                            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-card p-4">
                                <p className="text-yellow-300 text-sm">{usageReportError}</p>
                            </div>
                        ) : !usageReport ? (
                            <div className="text-snapfy-text-secondary">Carregando dados de consumo...</div>
                        ) : (
                            <>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                    {usageReport.usageByAction?.map((action: any) => (
                                        <Card key={action.actionType} className="p-4">
                                            <div className="text-sm text-snapfy-text-secondary capitalize">{action.actionType.replace('_', ' ')}</div>
                                            <div className="text-2xl font-bold text-snapfy-text-primary">{action.count} <span className="text-xs font-normal text-snapfy-text-secondary">gerados</span></div>
                                            <div className="text-sm text-snapfy-accent-soft">{action.totalCredits} créditos</div>
                                        </Card>
                                    ))}
                                </div>

                                {/* Visual consumption chart */}
                                {usageReport.topConsumers && usageReport.topConsumers.length > 0 && (
                                    <div className="mb-8">
                                        <h4 className="text-md font-semibold mb-4 text-snapfy-text-secondary">Consumo por Membro</h4>
                                        <div className="space-y-3">
                                            {usageReport.topConsumers.map((user: any) => {
                                                const maxConsumed = Math.max(...usageReport.topConsumers.map((u: any) => u.total_consumed || 0), 1);
                                                const percentage = ((user.totalConsumed || 0) / maxConsumed) * 100;
                                                return (
                                                    <div key={user.userId} className="flex items-center gap-3">
                                                        <div className="w-32 text-sm text-snapfy-text-secondary truncate" title={user.userName}>
                                                            {user.userName}
                                                        </div>
                                                        <div className="flex-1 bg-snapfy-inner rounded-full h-6 overflow-hidden">
                                                            {/* Gradient uses snapfy-primary → snapfy-accent-soft token names as color stops;
                                                                these are Tailwind utilities (from-snapfy-primary to-snapfy-accent-soft), not raw hex. */}
                                                            <div
                                                                className="h-full bg-gradient-to-r from-snapfy-primary to-snapfy-accent-soft rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                                                style={{ width: `${Math.max(percentage, 8)}%` }}
                                                            >
                                                                <span className="text-xs font-medium text-white whitespace-nowrap">
                                                                    {user.totalConsumed} cr
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="w-16 text-xs text-snapfy-text-secondary text-right">
                                                            {user.transactionCount} ops
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <h4 className="text-md font-semibold mb-4 text-snapfy-text-secondary">Detalhamento por Membro</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="text-snapfy-text-secondary border-b border-snapfy-border text-sm">
                                                <th className="text-left py-3">Usuário</th>
                                                <th className="text-left py-3">Email</th>
                                                <th className="text-left py-3">Total Consumido</th>
                                                <th className="text-left py-3">Operações</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm">
                                            {usageReport.topConsumers?.map((user: any) => (
                                                <tr key={user.userId} className="border-b border-snapfy-border/50">
                                                    <td className="py-3 font-medium text-snapfy-text-primary">{user.userName}</td>
                                                    <td className="py-3 text-snapfy-text-secondary">{user.userEmail}</td>
                                                    <td className="py-3 text-snapfy-accent-soft font-bold">{user.totalConsumed} créditos</td>
                                                    <td className="py-3 text-snapfy-text-secondary">{user.transactionCount}</td>
                                                </tr>
                                            ))}
                                            {(!usageReport.topConsumers || usageReport.topConsumers.length === 0) && (
                                                <tr>
                                                    <td colSpan={4} className="py-4 text-center text-snapfy-text-secondary">Nenhum consumo registrado no período.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'billing' && authUser?.companyRole === 'admin' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold">Faturamento e Planos</h3>
                            <button
                                onClick={() => navigate('/profile?tab=purchase')}
                                className="text-snapfy-accent-soft text-sm hover:underline focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                            >
                                Comprar Créditos Avulsos →
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {plans.map((plan) => (
                                <Card key={plan.id} interactive className="p-6">
                                    <h4 className="text-xl font-bold mb-2">{plan.name}</h4>
                                    <p className="text-snapfy-text-secondary text-sm mb-4">{plan.description}</p>
                                    <div className="text-3xl font-bold mb-4">
                                        R$ {plan.price}
                                        <span className="text-sm text-snapfy-text-secondary font-normal">/{plan.duration_unit}</span>
                                    </div>
                                    <ul className="space-y-2 mb-6">
                                        <li className="flex items-center text-sm">
                                            <span className="text-green-400 mr-2">✓</span>
                                            {plan.monthly_credits} créditos
                                        </li>
                                        {/* Placeholder for fetching features */}
                                        <li className="flex items-center text-sm">
                                            <span className="text-green-400 mr-2">✓</span>
                                            Suporte Prioritário
                                        </li>
                                    </ul>
                                    <Button
                                        variant="solid"
                                        className="w-full"
                                        onClick={() => handleSubscribe(plan.id)}
                                    >
                                        Assinar Plano
                                    </Button>
                                </Card>
                            ))}
                        </div>

                        {plans.length === 0 && (
                            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-card p-4 mb-6 text-center">
                                <p className="text-yellow-300">
                                    Nenhum plano empresarial disponível no momento.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>

            {/* Invite Modal — conditionally mounted so local form state resets on close (original behavior). holds unsaved data: closeOnEsc/closeOnOverlayClick false */}
            {showInviteModal && (
                <InviteUserModal
                    isOpen
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={fetchCompany}
                />
            )}

            {/* Create User Modal — conditionally mounted so local form/success state resets on close (original behavior). holds unsaved data: closeOnEsc/closeOnOverlayClick false */}
            {showCreateUserModal && (
                <CreateUserModal
                    isOpen
                    onClose={() => setShowCreateUserModal(false)}
                    onSuccess={fetchCompany}
                />
            )}
        </div>
    );
};

// Invite Modal — uses Modal primitive with closeOnEsc/closeOnOverlayClick false (unsaved data guard)
const InviteUserModal: React.FC<{ isOpen: boolean; onClose: () => void; onSuccess: () => void }> = ({ isOpen, onClose, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/company/users/invite', { email });
            toast.success('Convite enviado!');
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erro ao enviar convite.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Convidar Usuário"
            closeOnEsc={false}
            closeOnOverlayClick={false}
            footer={
                <>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
                    <Button
                        variant="solid"
                        type="submit"
                        form="invite-user-form"
                        isLoading={loading}
                        disabled={loading}
                    >
                        Enviar Convite
                    </Button>
                </>
            }
        >
            <form id="invite-user-form" onSubmit={handleSubmit}>
                <Input
                    type="email"
                    id="invite-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email do usuário"
                    required
                />
            </form>
        </Modal>
    );
};

// Create User Modal — uses Modal primitive with closeOnEsc/closeOnOverlayClick false (unsaved data guard)
const CreateUserModal: React.FC<{ isOpen: boolean; onClose: () => void; onSuccess: () => void }> = ({ isOpen, onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('member');
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/company/users', { name, email, role });
            setCreated(true);
            onSuccess();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erro ao criar usuário.');
        } finally {
            setLoading(false);
        }
    };

    if (created) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title="Usuário Criado!"
                closeOnEsc={false}
                closeOnOverlayClick={false}
                footer={
                    <Button variant="solid" className="w-full" onClick={onClose}>Fechar</Button>
                }
            >
                <div className="text-center py-2">
                    <svg className="w-12 h-12 text-green-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-snapfy-text-secondary mb-1">Uma senha temporária foi enviada para:</p>
                    <p className="text-snapfy-text-primary font-medium mb-4">{email}</p>
                    <p className="text-snapfy-text-secondary text-sm">O usuário poderá alterar a senha na aba Segurança do perfil após o primeiro login.</p>
                </div>
            </Modal>
        );
    }

    const roleOptions = [
        { value: 'member', label: 'Membro' },
        { value: 'admin', label: 'Administrador' },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Criar Novo Usuário"
            closeOnEsc={false}
            closeOnOverlayClick={false}
            footer={
                <>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
                    <Button
                        variant="solid"
                        type="submit"
                        form="create-user-form"
                        isLoading={loading}
                        disabled={loading}
                    >
                        Criar Usuário
                    </Button>
                </>
            }
        >
            <form id="create-user-form" onSubmit={handleSubmit} className="space-y-3">
                <Input
                    type="text"
                    id="create-user-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nome completo"
                    required
                />
                <Input
                    type="email"
                    id="create-user-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                />
                <Select
                    options={roleOptions}
                    value={role}
                    onChange={(value) => setRole(value)}
                />
            </form>
        </Modal>
    );
};

export default OrganizationPage;
